<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue';
import { FIELD_TYPES, FormFieldType, type FormField } from '@/models';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

/**
 * A field navigator for long entry forms: a always-visible box that filters the form's fields as you type and jumps to
 * the one you pick. It is a search *and* a table of contents — focusing it with an empty query lists the whole form —
 * so the list stays reachable without a click-to-reveal step.
 */
const props = defineProps({
  fields: {
    type: Object as PropType<FormField[]>,
    required: true
  }
});

/** Emits the field itself rather than an index: the rendered list is filtered and its sections can be collapsed, so
 * only the entry page can turn a field into a scroll position. */
const emits = defineEmits(['onFieldSelected']);

/** Layout-only items — they carry no label worth jumping to. */
const NON_NAVIGABLE_TYPES: FormFieldType[] = [
  FormFieldType.BRANDING,
  FormFieldType.SUBMIT_BUTTON
];

/** Enough to scroll through, few enough to keep the dropdown cheap on 200-field forms. Past this the count line tells
 * the user to keep typing. */
const MAX_RESULTS = 50;

interface LabelPart {
  text: string;
  matched: boolean;
}

interface FieldMatch {
  field: FormField;
  sectionLabel?: string;
  parts: LabelPart[];
}

const rootRef = ref<HTMLElement>();
const inputRef = ref<HTMLInputElement>();
const listRef = ref<HTMLElement>();

const query = ref('');
const panelOpen = ref(false);
const activeIndex = ref(0);

const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);
const shortcutHint = isMac ? '⌘K' : 'Ctrl K';

/** Sections own their children by identifier, so this is the only way back from a field to the section it sits in when
 * the field itself carries no `sectionId`. */
const sectionLabels = computed(() => {
  const labels = new Map<string, string>();
  props.fields.forEach(field => {
    if (field.type !== FormFieldType.SECTION) {
      return;
    }
    (field.options?.fields ?? []).forEach(identifier => labels.set(identifier, field.label));
  });
  return labels;
});

const navigableFields = computed(() =>
  props.fields.filter(
    field =>
      field.show !== false &&
      !NON_NAVIGABLE_TYPES.includes(field.type) &&
      (field.label ?? '').trim().length > 0
  )
);

const tokens = computed(() =>
  query.value
    .toLowerCase()
    .split(/\s+/)
    .filter(token => token.length > 0)
);

/** Lower is better. Ranks by how the query sits in the label — a prefix beats a word start, which beats a match buried
 * mid-word, which beats matching only the variable name. */
const rankOf = (label: string, variableName: string, searchText: string) => {
  if (label.startsWith(searchText)) {
    return 0;
  }
  // The leading space makes this a word-start test without a regex.
  if (` ${label}`.includes(` ${searchText}`)) {
    return 1;
  }
  if (label.includes(searchText)) {
    return 2;
  }
  if (variableName.includes(searchText)) {
    return 3;
  }
  // Every token hit somewhere, but not as one run.
  return 4;
};

/** Splits a label into matched / unmatched runs so the dropdown can highlight what the query hit. */
const splitLabel = (label: string): LabelPart[] => {
  if (tokens.value.length === 0) {
    return [{ text: label, matched: false }];
  }

  const lowerLabel = label.toLowerCase();
  const ranges: [number, number][] = [];
  tokens.value.forEach(token => {
    const start = lowerLabel.indexOf(token);
    if (start > -1) {
      ranges.push([start, start + token.length]);
    }
  });
  if (ranges.length === 0) {
    return [{ text: label, matched: false }];
  }

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  ranges.forEach(range => {
    const previous = merged[merged.length - 1];
    if (previous && range[0] <= previous[1]) {
      previous[1] = Math.max(previous[1], range[1]);
    } else {
      merged.push([...range] as [number, number]);
    }
  });

  const parts: LabelPart[] = [];
  let cursor = 0;
  merged.forEach(([start, end]) => {
    if (start > cursor) {
      parts.push({ text: label.slice(cursor, start), matched: false });
    }
    parts.push({ text: label.slice(start, end), matched: true });
    cursor = end;
  });
  if (cursor < label.length) {
    parts.push({ text: label.slice(cursor), matched: false });
  }
  return parts;
};

/** `total` is the unclipped count — `matches` is what the panel renders. */
const results = computed<{ matches: FieldMatch[]; total: number }>(() => {
  const searchTokens = tokens.value;
  const searchText = searchTokens.join(' ');
  const scored: { field: FormField; rank: number; order: number }[] = [];

  navigableFields.value.forEach((field, order) => {
    const label = (field.label ?? '').toLowerCase();
    const variableName = (field.variableName ?? '').toLowerCase();
    if (
      searchTokens.length > 0 &&
      !searchTokens.every(token => label.includes(token) || variableName.includes(token))
    ) {
      return;
    }
    scored.push({
      field,
      order,
      rank: searchTokens.length > 0 ? rankOf(label, variableName, searchText) : 0
    });
  });

  // Ties keep form order, so an unfiltered list reads as the form's table of contents.
  scored.sort((a, b) => a.rank - b.rank || a.order - b.order);

  return {
    total: scored.length,
    matches: scored.slice(0, MAX_RESULTS).map(({ field }) => ({
      field,
      sectionLabel: field.sectionId ?
        props.fields.find(item => item.identifier === field.sectionId)?.label :
        sectionLabels.value.get(field.identifier),
      parts: splitLabel(field.label ?? '')
    }))
  };
});

const iconFor = (field: FormField) => {
  const fieldType = FIELD_TYPES.find(type => type.type === field.type);
  return [fieldType?.iconStyle ?? 'far', fieldType?.icon ?? 'text'];
};

const scrollActiveIntoView = async () => {
  await nextTick();
  const activeRow = listRef.value?.children[activeIndex.value] as HTMLElement | undefined;
  activeRow?.scrollIntoView({ block: 'nearest' });
};

const moveActive = (step: number) => {
  const { matches } = results.value;
  if (matches.length === 0) {
    return;
  }
  // The first arrow press on a closed panel only reopens it, so the highlight never jumps unseen.
  if (!panelOpen.value) {
    panelOpen.value = true;
    return;
  }
  activeIndex.value = (activeIndex.value + step + matches.length) % matches.length;
  scrollActiveIntoView();
};

const selectMatch = (match: FieldMatch) => {
  emits('onFieldSelected', match.field);
  panelOpen.value = false;
  // The query survives so the same result set is one focus away, but focus goes back to the form the user just jumped to.
  inputRef.value?.blur();
};

const clearQuery = () => {
  query.value = '';
  inputRef.value?.focus();
};

const onKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      moveActive(1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      moveActive(-1);
      break;
    case 'Enter': {
      const match = results.value.matches[activeIndex.value];
      if (panelOpen.value && match) {
        event.preventDefault();
        selectMatch(match);
      }
      break;
    }
    case 'Escape':
      // Escape unwinds the search one step at a time, and is swallowed while there is something to unwind so it does
      // not reach the entry dialog and close the whole form.
      if (panelOpen.value) {
        event.stopPropagation();
        panelOpen.value = false;
      } else if (query.value) {
        event.stopPropagation();
        query.value = '';
      } else {
        inputRef.value?.blur();
      }
      break;
    case 'Tab':
      panelOpen.value = false;
      break;
    default:
      break;
  }
};

const focusSearch = () => {
  inputRef.value?.focus();
  inputRef.value?.select();
  panelOpen.value = true;
};

const onGlobalKeydown = (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    focusSearch();
  }
};

/** Closing on document mousedown rather than on blur keeps the panel up while its own scrollbar is dragged. */
const onDocumentMouseDown = (event: MouseEvent) => {
  if (!rootRef.value?.contains(event.target as Node)) {
    panelOpen.value = false;
  }
};

watch(query, () => {
  activeIndex.value = 0;
  panelOpen.value = true;
});

onMounted(() => {
  window.addEventListener('keydown', onGlobalKeydown);
  document.addEventListener('mousedown', onDocumentMouseDown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown);
  document.removeEventListener('mousedown', onDocumentMouseDown);
});
</script>

<template>
  <div class="field-search" ref="rootRef">
    <div class="search-box" :class="{ 'search-box-focused': panelOpen }">
      <i class="pi pi-search search-icon" />
      <input ref="inputRef"
        v-model="query"
        type="text"
        class="search-input"
        placeholder="Search fields"
        role="combobox"
        aria-label="Search fields"
        aria-controls="field-search-results"
        :aria-expanded="panelOpen"
        @focus="panelOpen = true"
        @keydown="onKeydown"/>
      <span class="result-count" v-if="query && panelOpen">
        {{ results.total }}
      </span>
      <button type="button"
        class="icon-button"
        aria-label="Clear search"
        v-if="query"
        @click="clearQuery">
        <i class="pi pi-times" />
      </button>
      <span class="shortcut-hint" v-else>{{ shortcutHint }}</span>
    </div>

    <div class="results-panel"
id="field-search-results"
role="listbox"
v-if="panelOpen">
      <ul class="results" ref="listRef" v-if="results.matches.length > 0">
        <li v-for="(match, index) in results.matches"
          :key="match.field.identifier"
          class="result"
          role="option"
          :aria-selected="index === activeIndex"
          :class="{ 'result-active': index === activeIndex }"
          @mousemove="activeIndex = index"
          @mousedown.prevent="selectMatch(match)">
          <FontAwesomeIcon :icon="iconFor(match.field)" class="result-icon" />
          <span class="result-label">
            <template v-for="(part, partIndex) in match.parts" :key="partIndex">
              <mark v-if="part.matched">{{ part.text }}</mark>
              <template v-else>{{ part.text }}</template>
            </template>
          </span>
          <span class="result-section" v-if="match.sectionLabel">{{ match.sectionLabel }}</span>
        </li>
      </ul>

      <div class="results-empty" v-else>
        No fields match “{{ query }}”
      </div>

      <div class="results-footer" v-if="results.total > results.matches.length">
        Showing {{ results.matches.length }} of {{ results.total }} — keep typing to narrow
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../../assets/variables.module' as *;

.field-search {
  position: relative;
  width: 100%;
  max-width: 380px;
}
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 8px 0 10px;
  background-color: #fff;
  border: 1px solid #ced3d9;
  border-radius: 6px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #adb5bd;
  }

  &.search-box-focused {
    border-color: $primary-color;
    box-shadow: 0 0 0 3px $primary-light;
  }
}
.search-icon {
  font-size: 13px;
  color: #6b7683;
}
.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #2a3a4b;

  &::placeholder {
    color: #8a939e;
  }
}
.result-count {
  flex-shrink: 0;
  font-size: 12px;
  color: #6b7683;
  font-variant-numeric: tabular-nums;
}
.shortcut-hint {
  flex-shrink: 0;
  padding: 1px 5px;
  border: 1px solid #dfe3e8;
  border-radius: 4px;
  font-size: 11px;
  color: #8a939e;
  white-space: nowrap;
}
.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: #6b7683;
  cursor: pointer;

  &:hover {
    background-color: #eef1f4;
    color: #2a3a4b;
  }

  .pi {
    font-size: 11px;
  }
}
.results-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 10;
  background-color: #fff;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(15, 15, 15, 0.12);
  overflow: hidden;
}
.results {
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 320px;
  overflow-y: auto;
}
.result {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;

  &.result-active {
    background-color: rgba($primary-color, 0.08);
  }

  .result-icon {
    flex-shrink: 0;
    width: 14px;
    font-size: 13px;
    color: #6b7683;
  }

  .result-label {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: #2a3a4b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    mark {
      background-color: rgba($primary-color, 0.14);
      color: $primary-color;
      font-weight: 600;
      border-radius: 2px;
    }
  }

  .result-section {
    flex-shrink: 0;
    max-width: 40%;
    font-size: 12px;
    color: #8a939e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.results-empty {
  padding: 14px 12px;
  font-size: 13px;
  color: #6b7683;
}
.results-footer {
  padding: 6px 12px;
  border-top: 1px solid #eef1f4;
  background-color: #f8f9fa;
  font-size: 12px;
  color: #8a939e;
}

@media (max-width: 600px) {
  .field-search {
    max-width: none;
  }
  .shortcut-hint {
    display: none;
  }
}
</style>
